import { FC, useCallback, useEffect } from 'react';
import RefinementEmptyList from './RefinementEmptyList';
import { useTranslation } from '~/shared/utils/translation';
import { RefinementListRow } from '~/features/productList/components/RefinementListRow';
import { VirtualizedList } from '~/features/productList/components/VirtualizedList';
import { RefinementProps } from './RefinementProps';
import { useDebounce } from 'react-use';
import useOptimizedRefinementList from '~/shared/hooks/useOptimizedRefinementList';
import { usePinnedRefinementItems } from '~/features/productList/hooks/usePinnedRefinementItems';
import { useDrawerFilterTracking } from '~/features/productList/hooks/useDrawerFilterTracking';

type Props = Omit<RefinementProps, 'label'> & {
    showCount?: boolean;
    searchText?: string;
    onRefine?: (value: string) => void;
    pinSelection?: boolean;
    isOpen?: boolean;
};

export const RefinementList: FC<Props> = (props) => {
    const {
        attribute,
        showCount,
        isVisible = true,
        searchText,
        onRefine,
        pinSelection,
        isOpen = false,
    } = props;
    const { translate } = useTranslation();

    const {
        refine,
        searchForItems,
        allItems: fetchedItems,
    } = useOptimizedRefinementList({
        attribute,
    });
    const pinnedItems = usePinnedRefinementItems(fetchedItems, searchText ?? '', isOpen);
    const allItems = pinSelection ? pinnedItems : fetchedItems;

    const getLabel = useCallback(
        (label: string) => {
            switch (label) {
                case 'true':
                    return translate('pdp.spec.true');
                case 'false':
                    return translate('pdp.spec.false');
                default:
                    return label;
            }
        },
        [translate],
    );

    const { trackFilter } = useDrawerFilterTracking();
    const handleRefine = useCallback(
        (value: string) => {
            if (!value) return;
            trackFilter(attribute, value, {
                isRefined: allItems.find((item) => item.value === value)?.isRefined,
            });

            refine(value);
            onRefine?.(value);
        },
        [attribute, allItems, onRefine],
    );

    useDebounce(
        () => {
            searchForItems(searchText ?? '');
        },
        200,
        [searchText],
    );

    useEffect(() => {
        if (searchText) {
            searchForItems(searchText ?? '');
        }
    }, [searchText, allItems]);

    if (!isVisible) return null;

    if (allItems.length === 0) return <RefinementEmptyList />;

    return (
        <VirtualizedList
            items={allItems}
            itemKey={(item) => `${item.label}-${item.value}`}
            renderItem={(item) => (
                <RefinementListRow
                    label={getLabel(item.label)}
                    value={item.value}
                    count={item.count}
                    isRefined={item.isRefined}
                    shouldShowCount={showCount}
                    onRefine={handleRefine}
                />
            )}
        />
    );
};

export default RefinementList;
